import React from 'react';
import {
  type WeeklySchedule,
  type Weekday,
} from '@/features/plants/schemas/schedule-plant.schema.ts';
import { type ScheduleTimeSlotWindow } from './schedule-time-slot-window.tsx';
import {
  SCHEDULE_TIME_SLOT_WINDOW_TOP_OFFSET,
  SCHEDULE_TIME_SLOT_WINDOW_HEIGHT_OFFSET,
  SCHEDULE_HOURS_IN_DAY,
  SCHEDULE_MINUTES_IN_HOUR,
} from '@/features/plants/components/plant-schedule/schedule/constants.ts';
import {
  sortTimeSlotsByStartTime,
  convertTimeToMinutes,
} from '@/features/plants/components/plant-schedule/schedule/utils/time.ts';

export function createHoursLabels(): string[] {
  return Array.from({ length: SCHEDULE_HOURS_IN_DAY }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });
}

export function getTotalScheduleWidth(
  hoursRefs: React.RefObject<{ [key in Weekday]?: HTMLDivElement | null }>
): number {
  const anyDayEl = Object.values(hoursRefs.current || {}).find(
    (el): el is HTMLDivElement => !!el
  );
  if (!anyDayEl) return 0;
  return anyDayEl.getBoundingClientRect().width;
}

export function convertPixelsToMinutes(
  pixelDistance: number,
  hoursRefs: React.RefObject<{ [key in Weekday]?: HTMLDivElement | null }>,
  snapToMinutes: number
): number {
  const totalWidth = getTotalScheduleWidth(hoursRefs);
  if (totalWidth <= 0) return 0;
  const minutesInDay = SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR;
  const rawMinutes = (pixelDistance / totalWidth) * minutesInDay;
  const snappedMinutes = Math.round(rawMinutes / snapToMinutes) * snapToMinutes;
  return snappedMinutes;
}

export function calculateTimeSlotPositions({
  weeklySchedule,
  gridEl,
  dayRefs,
  hoursRefs,
}: {
  weeklySchedule: WeeklySchedule;
  gridEl: HTMLDivElement | null;
  dayRefs: React.RefObject<{ [key in Weekday]?: HTMLDivElement | null }>;
  hoursRefs: React.RefObject<{ [key in Weekday]?: HTMLDivElement | null }>;
}): ScheduleTimeSlotWindow[] {
  if (!gridEl) return [];

  const timeSlotWindows: ScheduleTimeSlotWindow[] = [];
  const gridRect = gridEl.getBoundingClientRect();
  const scheduleWidth = getTotalScheduleWidth(hoursRefs);
  if (scheduleWidth <= 0) return [];

  Object.entries(weeklySchedule).forEach(([day, timeSlots]) => {
    const weekday = day as Weekday;
    const dayElement = dayRefs.current?.[weekday];
    if (!dayElement || !timeSlots) return;

    const dayRect = dayElement.getBoundingClientRect();
    const dayTop = dayRect.top - gridRect.top;
    const dayHeight = dayRect.height;

    const sortedTimeSlots = sortTimeSlotsByStartTime(timeSlots);

    sortedTimeSlots.forEach((timeSlot) => {
      const startTimeInMinutes = convertTimeToMinutes(timeSlot.startTime);
      const endTimeInMinutes = convertTimeToMinutes(timeSlot.endTime);

      const totalMinutesInDay = SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR;
      const startPercentage = startTimeInMinutes / totalMinutesInDay;
      const endPercentage = endTimeInMinutes / totalMinutesInDay;

      const windowLeft = startPercentage * scheduleWidth;
      const windowWidth = (endPercentage - startPercentage) * scheduleWidth;

      timeSlotWindows.push({
        slot: timeSlot,
        day: weekday,
        top: dayTop + SCHEDULE_TIME_SLOT_WINDOW_TOP_OFFSET,
        left: windowLeft,
        width: windowWidth,
        height: dayHeight + SCHEDULE_TIME_SLOT_WINDOW_HEIGHT_OFFSET,
      });
    });
  });

  return timeSlotWindows;
}
